import React, { useCallback, useState, useMemo, useEffect } from 'react';
import { useMutation } from '@apollo/client';
import { useCartContext } from '../../peregrine/lib/context/cart';
import { useWindowSize, useToasts } from '../../peregrine';
import { useAppContext } from '../../peregrine/lib/context/app';
import { useAwaitQuery } from '../../peregrine/lib/hooks/useAwaitQuery';
import { appendOptionsToPayload } from '../../peregrine/lib/util/appendOptionsToPayload';
import { findMatchingVariant } from '../../peregrine/lib/util/findMatchingProductVariant';
import { isProductConfigurable } from '../../peregrine/lib/util/isProductConfigurable';
import { deriveErrorMessage } from '../../peregrine/lib/util/deriveErrorMessage';
import {
    isProductConfigurator,
    isProductRenderable
} from '../../peregrine/lib/util/isProductConfigurator';
import Icon from '../../components/Icon';
import { useUserContext } from '../../peregrine/lib/context/user';
import { useHistory } from 'react-router-dom';

const INITIAL_OPTION_CODES = new Map();
const INITIAL_OPTION_SELECTIONS = new Map();
const INITIAL_QUANTITY = 1;

import { AlertCircle as AlertCircleIcon } from 'react-feather';
const errorIcon = <Icon src={AlertCircleIcon} attrs={{ width: 18 }} />;

const deriveOptionCodesFromProduct = product => {
    // If this is a simple product it has no option codes.
    if (!isProductConfigurable(product)) {
        return INITIAL_OPTION_CODES;
    }

    // Initialize optionCodes based on the options of the product.
    const initialOptionCodes = new Map();
    for (const {
        attribute_id,
        attribute_code
    } of product.configurable_options) {
        initialOptionCodes.set(attribute_id, attribute_code);
    }

    return initialOptionCodes;
};

// Similar to deriving the initial codes for each option.
const deriveOptionSelectionsFromProduct = product => {
    if (!isProductConfigurable(product)) {
        return INITIAL_OPTION_SELECTIONS;
    }

    const initialOptionSelections = new Map();
    for (const { attribute_id } of product.configurable_options) {
        initialOptionSelections.set(attribute_id, undefined);
    }

    return initialOptionSelections;
};

const getIsMissingOptions = (product, optionSelections) => {
    // Non-configurable products can't be missing options.
    if (!isProductConfigurable(product)) {
        return false;
    }

    // Configurable products are missing options if we have fewer
    // option selections than the product has options.
    const { configurable_options } = product;
    const numProductOptions = configurable_options.length;
    const numProductSelections = Array.from(optionSelections.values()).filter(
        value => !!value
    ).length;

    return numProductSelections < numProductOptions;
};

const getMediaGalleryEntries = (product, optionCodes, optionSelections) => {
    let value = [];

    const { media_gallery_entries, variants } = product;
    const isConfigurable = isProductConfigurable(product);

    // Selections are initialized to "code => undefined". Once we select a value, like color, the selections change. This filters out unselected options.
    const optionsSelected =
        Array.from(optionSelections.values()).filter(value => !!value).length >
        0;

    if (!isConfigurable || !optionsSelected) {
        value = media_gallery_entries;

        // if the product is a configurator, we
        // prepend a fake image to the media gallery
        // to use as canvas renderer
        if (isProductRenderable(product)) {
            value = [
                ...value,
                {
                    __typename: 'MediaImageRendered',
                    id: -1,
                    label: 'live',
                    position: -1,
                    disabled: false,
                    file: 'canvasBackground.png'
                }
            ];
        }
    } else {
        // If any of the possible variants matches the selection add that
        // variant's image to the media gallery. NOTE: This _can_, and does,
        // include variants such as size. If Magento is configured to display
        // an image for a size attribute, it will render that image.
        const item = findMatchingVariant({
            optionCodes,
            optionSelections,
            variants
        });

        value = item
            ? [...item.product.media_gallery_entries, ...media_gallery_entries]
            : media_gallery_entries;
    }

    return value;
};

// We only want to display breadcrumbs for one category on a PDP even if a
// product has multiple related categories. This function filters and selects
// one category id for that purpose.
const getBreadcrumbCategoryId = categories => {
    // filter out category #3 ("Todos los productos")
    const storeCategoryPath = '1/' + STORE_VIEW_ROOT_CATEGORY_ID + '/';
    // https://youngdogs.atlassian.net/browse/LC-337
    const candidates =
        categories &&
        categories.filter(
            category =>
                category.id > 3 && category.path.startsWith(storeCategoryPath)
        );

    // Exit if there are no categories for this product.
    if (!candidates || !candidates.length) {
        return;
    }

    const breadcrumbSet = new Set();

    candidates.forEach(({ breadcrumbs }) => {
        // breadcrumbs can be `null`...
        (breadcrumbs || []).forEach(({ category_id }) =>
            breadcrumbSet.add(category_id)
        );
    });

    // https://youngdogs.atlassian.net/browse/ZNSVR-680
    const leafCategory = candidates
        .filter(cat => cat.breadcrumbs)
        .sort((a, b) => a.path.localeCompare(b.path));

    // Until we can get the single canonical breadcrumb path to a product we
    // will just return the first category id of the potential leaf categories.
    // const leafCategory = candidates.find(
    //     category => !breadcrumbSet.has(category.id)
    // );

    // If we couldn't find a leaf category then just use the first category
    // in the list for this product.
    return (leafCategory[0] && leafCategory[0].id) || candidates[0].id;
};

const getConfigPrice = (
    product,
    optionCodes,
    optionSelections,
    customerGroupId
) => {
    let value;

    const { variants } = product;
    const isConfigurable = isProductConfigurable(product);

    const optionsSelected =
        Array.from(optionSelections.values()).filter(value => !!value).length >
        0;

    if (!isConfigurable || !optionsSelected || !variants || !variants.length) {
        // Handle missing prices array
        if (!product.prices || !product.prices.length) {
            // Fall back to price_range if available
            if (product.price_range && product.price_range.minimum_price) {
                value = product.price_range.minimum_price.final_price.value;
            } else if (product.price) {
                // Last resort: use single price value
                value = product.price.value || product.price;
            } else {
                value = 0;
            }
        } else {
            const productPrices =
                product.prices.find(
                    prices => prices.customer_group_id == customerGroupId
                ) || product.prices.find(prices => prices.customer_group_id == 0);
            // value format: {"__typename":"Money","currency":"EUR","value":123456};
            if (productPrices && productPrices.minimum_price) {
                value = productPrices.minimum_price.final_price.value;
            } else {
                value = product.price?.value || 0;
            }
        }
    } else {
        const item = findMatchingVariant({
            optionCodes,
            optionSelections,
            variants
        });

        if (item && item.product && item.product.price_range) {
            value = item.product.price_range.minimum_price.final_price.value;
        } else if (product.price_range && product.price_range.minimum_price) {
            value = product.price_range.minimum_price.regular_price.value;
        } else {
            value = product.price?.value || 0;
        }
    }

    // Safe currency access
    const currency = product.price_range?.minimum_price?.regular_price?.currency 
        || product.price?.currency 
        || 'EUR';

    return {
        currency: currency,
        value: value
    };
};

const getOrderGridPrice = (product, orderGridItems) => {
    const totalPrice = orderGridItems.reduce(
        (accumulator, item) => accumulator + item.custom_price * item.quantity,
        0
    );

    return {
        currency: product.price_range.minimum_price.regular_price.currency,
        value: totalPrice
    };
};

const SUPPORTED_PRODUCT_TYPES = ['SimpleProduct', 'ConfigurableProduct'];

/**
 * @param {GraphQLQuery} props.addConfigurableProductToCartMutation - configurable product mutation
 * @param {GraphQLQuery} props.addSimpleProductToCartMutation - configurable product mutation
 * @param {Object} props.product - the product, see RootComponents/Product
 *
 * @returns {{
 *  breadcrumbCategoryId: string|undefined,
 *  errorMessage: string|undefined,
 *  handleAddToCart: func,
 *  handleSelectionChange: func,
 *  handleSetQuantity: func,
 *  isAddToCartDisabled: boolean,
 *  mediaGalleryEntries: array,
 *  productDetails: object,
 *  quantity: number
 * }}
 */
export const useProductFullDetail = props => {
    const {
        addConfigurableProductToCartMutation,
        addSimpleProductToCartMutation,
        addMultipleSimpleProductToCartMutation,
        createCartMutation,
        getCartDetailsQuery,
        product
    } = props;

    const productType = product.__typename;

    const isSupportedProductType = SUPPORTED_PRODUCT_TYPES.includes(
        productType
    );

    const {
        location: { pathname }
    } = useHistory();

    const [{ isSignedIn, currentUser }] = useUserContext();
    const customerGroupId = isSignedIn ? currentUser.group_id : 0;

    const useOrderGrid =
        isProductConfigurable(product) &&
        (product.yd_display_mode == 1 ||
            (product.yd_display_mode == 2 && isSignedIn));

    const [, { toggleDrawer }] = useAppContext();
    let [{ isAddingItem }, { addItemToCart }] = useCartContext();

    const [addConfigurableProductToCart] = useMutation(
        addConfigurableProductToCartMutation
    );

    const [addSimpleProductToCart] = useMutation(
        addSimpleProductToCartMutation
    );

    const [addMultipleSimpleProductToCart] = useMutation(
        addMultipleSimpleProductToCartMutation
    );

    const [fetchCartId] = useMutation(createCartMutation);

    const fetchCartDetails = useAwaitQuery(getCartDetailsQuery);

    const [quantity, setQuantity] = useState(INITIAL_QUANTITY);

    const [orderGridItems, setOrderGridItems] = useState([]);

    const breadcrumbCategoryId = useMemo(
        () => getBreadcrumbCategoryId(product.categories),
        [product.categories]
    );

    const derivedOptionSelections = useMemo(
        () => deriveOptionSelectionsFromProduct(product),
        [product]
    );

    const [optionSelections, setOptionSelections] = useState(
        derivedOptionSelections
    );

    const derivedOptionCodes = useMemo(
        () => deriveOptionCodesFromProduct(product),
        [product]
    );
    const [optionCodes] = useState(derivedOptionCodes);

    const stockMessage = useMemo(() => {
        const isConfigurable = isProductConfigurable(product);
        if (!isConfigurable) {
            return product.stock_message;
        }

        // Safety check for variants
        if (!product.variants || !product.variants.length) {
            return product.stock_message || '';
        }

        // Selections are initialized to "code => undefined". Once we select a value, like color, the selections change. This filters out unselected options.
        const allOptionsSelected =
            Array.from(optionSelections.values()).filter(value => !!value)
                .length == optionSelections.size;

        if (!allOptionsSelected) {
            return '';
        }

        let candidates = product.variants;

        for (const [option_id, value] of optionSelections) {
            const attributeCode = optionCodes.get(option_id);
            candidates = candidates.filter(variant => {
                // Safety check for variant.attributes
                if (!variant.attributes || !variant.attributes.length) {
                    return false;
                }
                return variant.attributes.find(
                    attribute =>
                        attribute.code == attributeCode &&
                        attribute.value_index == value
                );
            });
        }

        if (candidates.length) {
            return candidates[0].product.stock_message;
        }
        return product.stock_message;
    }, [optionSelections]);

    const isMissingOptions = useMemo(
        () =>
            useOrderGrid
                ? false
                : getIsMissingOptions(product, optionSelections),
        [product, optionSelections]
    );

    const isValidConfigurableCombination = useMemo(() => {
        if (!isProductConfigurable(product) || useOrderGrid) {
            return true;
        }

        // Selections are initialized to "code => undefined". Once we select a value, like color, the selections change. This filters out unselected options.
        const allOptionsSelected =
            Array.from(optionSelections.values()).filter(value => !!value)
                .length == optionSelections.size;
        if (!allOptionsSelected) {
            return true;
        }

        const item = findMatchingVariant({
            optionCodes,
            optionSelections,
            variants: product.variants
        });

        return !!item;
    }, [product, optionSelections, useOrderGrid]);

    const mediaGalleryEntries = useMemo(
        () => getMediaGalleryEntries(product, optionCodes, optionSelections),
        [product, optionCodes, optionSelections]
    );

    const [, { addToast }] = useToasts();

    const windowSize = useWindowSize();

    const handleAddToCart = useCallback(
        async e => {
            const form = e.currentTarget.form;

            if (!form.checkValidity()) {
                const invalidElements = [...form.querySelectorAll(':invalid')];

                invalidElements.forEach(element =>
                    element.closest('fieldset').classList.add('invalid')
                );

                setIsFormSubmited(true);

                const isMobile = windowSize.innerWidth <= 960;

                const first = invalidElements.shift();
                isMobile &&
                    first &&
                    first.scrollIntoView({
                        behavior: 'smooth'
                    });

                return false;
            }

            const payload = {
                item: product,
                productType,
                quantity
            };

            if (isProductConfigurator(product)) {
                payload.customPrice = product.customPrice;
                payload.customOptionId = product.customOptionId;
                payload.customOptionValue = product.customOptionValue;
            } else if (isProductConfigurable(product)) {
                if (useOrderGrid) {
                    // fake item to force addItemToCart() to use set the correct variables
                    // to the mutation (see asyncActions.js)
                    payload.item = {
                        __typename: 'BundleProduct'
                    };

                    payload.cartItems = orderGridItems
                        .filter(item => item.quantity > 0)
                        .map(item => {
                            return {
                                data: item
                            };
                        });
                } else {
                    appendOptionsToPayload(
                        payload,
                        optionSelections,
                        optionCodes
                    );
                }
            }

            if (isSupportedProductType) {
                let addItemMutation;
                // Use the proper mutation for the type.
                if (productType === 'SimpleProduct') {
                    addItemMutation = addSimpleProductToCart;
                } else if (productType === 'ConfigurableProduct') {
                    if (useOrderGrid) {
                        addItemMutation = addMultipleSimpleProductToCart;
                    } else {
                        addItemMutation = addConfigurableProductToCart;
                    }
                }

                console.log('PAYLOAD', payload, {
                    ...payload,
                    addItemMutation,
                    fetchCartDetails,
                    fetchCartId
                });

                try {
                    await addItemToCart({
                        ...payload,
                        addItemMutation,
                        fetchCartDetails,
                        fetchCartId
                    });

                    toggleDrawer('cart');
                } catch (error) {
                    console.error(error);
                    const errorMessage = error.message.replace(
                        'Error: GraphQL error: ',
                        ''
                    );

                    addToast({
                        type: 'error',
                        icon: errorIcon,
                        message: errorMessage,
                        dismissable: true,
                        timeout: 10000
                    });
                }
            } else {
                console.error('Unsupported product type. Cannot add to cart.');
            }
        },
        [
            addConfigurableProductToCart,
            addItemToCart,
            addSimpleProductToCart,
            fetchCartDetails,
            fetchCartId,
            isSupportedProductType,
            optionCodes,
            optionSelections,
            orderGridItems,
            product,
            productType,
            quantity,
            toggleDrawer
        ]
    );

    const handleSelectionChange = useCallback(
        (optionId, selection) => {
            // We must create a new Map here so that React knows that the value
            // of optionSelections has changed.
            const nextOptionSelections = new Map([...optionSelections]);
            nextOptionSelections.set(optionId, selection);
            setOptionSelections(nextOptionSelections);
        },
        [optionSelections]
    );

    const handleSetQuantity = useCallback(
        value => {
            setQuantity(value);
        },
        [setQuantity]
    );

    const handleSetOrderGridItems = useCallback(
        (sku, quantity, price) => {
            const newOrderGridItems = [...orderGridItems];

            const item = newOrderGridItems.find(item => item.sku == sku);

            if (item) {
                item.quantity = quantity;
                item.custom_price = price;
            } else {
                newOrderGridItems.push({
                    sku: sku,
                    quantity: quantity,
                    custom_price: price
                });
            }

            setOrderGridItems(
                newOrderGridItems.filter(item => item.quantity > 0)
            );
        },
        [setOrderGridItems, orderGridItems]
    );

    /*
    useEffect(() => {
        if (orderGridItems.length == 0) {
            return;
        }

        
        
        if (document.location.href.indexOf('debugger') !== -1) {
            //const newOrderGridItems = [...orderGridItems];
            debugger
        }
    }, [isSignedIn]);
*/

    //
    const productPrice = useMemo(
        () =>
            useOrderGrid
                ? getOrderGridPrice(product, orderGridItems)
                : getConfigPrice(
                      product,
                      optionCodes,
                      optionSelections,
                      customerGroupId
                  ),
        [
            product,
            optionCodes,
            optionSelections,
            orderGridItems,
            useOrderGrid,
            customerGroupId
        ]
    );

    const productPrices = useMemo(
        () => {
            if (!product.prices || !product.prices.length) {
                return null;
            }
            return product.prices.find(
                prices => prices.customer_group_id == customerGroupId
            ) || product.prices.find(prices => prices.customer_group_id == 0);
        },
        [product, customerGroupId]
    );

    // refresh order grid items prices when login/logout
    useEffect(() => {
        if (!product.variants || !product.variants.length) {
            return;
        }

        const newOrderGridItems = [...orderGridItems];

        orderGridItems.forEach(orderGridItem => {
            const variant = product.variants.find(
                variant => variant.product.sku == orderGridItem.sku
            );
            
            if (!variant || !variant.product.prices || !variant.product.prices.length) {
                return;
            }
            
            const variantProductPrices = variant.product.prices.find(
                price => price.customer_group_id == customerGroupId
            );
            
            if (!variantProductPrices || !variantProductPrices.minimum_price) {
                return;
            }
            
            let variantPrice =
                variantProductPrices.minimum_price.final_price.value;

            if (variantProductPrices.price_tiers && variantProductPrices.price_tiers.length) {
                const applicablePriceTiers = variantProductPrices.price_tiers
                    .filter(tier => tier.quantity <= orderGridItem.quantity)
                    .sort((a, b) => b.quantity - a.quantity);

                if (applicablePriceTiers.length) {
                    variantPrice = applicablePriceTiers[0].final_price.value;
                }
            }

            orderGridItem.custom_price = variantPrice;
        });

        setOrderGridItems(newOrderGridItems);
    }, [product, isSignedIn, customerGroupId]);

    const resetConfigurator = useCallback(() => {
        document.location.href = pathname;
    }, [pathname]);

    // Normalization object for product details we need for rendering.
    const productDetails = {
        name: product.name,
        description: product.description,
        // productPrice is the calculated total price for the current product
        price: productPrice,
        prices: productPrices,
        sku: product.sku,
        attributes: product.attributes,
        related_products: product.related_products,
        stock_message: stockMessage,
        stock_status: product.stock_status,
        optionCodes,
        optionSelections,
        use_order_grid: useOrderGrid,
        short_description: product.short_description
            ? product.short_description.html
            : null
    };

    const isAddToCartDisabled =
        !isSupportedProductType ||
        isAddingItem ||
        isMissingOptions ||
        (useOrderGrid && orderGridItems.length == 0) ||
        !isValidConfigurableCombination ||
        product.stock_status == 'OUT_OF_STOCK' ||
        !(PUBLIC_PRICES || isSignedIn);

    return {
        breadcrumbCategoryId,
        handleAddToCart,
        handleSelectionChange,
        handleSetQuantity,
        handleSetOrderGridItems,
        isAddToCartDisabled,
        isValidConfigurableCombination,
        mediaGalleryEntries,
        productDetails,
        quantity,
        resetConfigurator
    };
};
